// AI-radar: wordt het merk geciteerd, onthouden en gekozen door AI en door AI-agents?
// Echte metingen met een kans en marge, per soort, aanbieder en vraagcluster. Niets wordt gepubliceerd;
// een daling wordt een voorstel in de wachtrij. Ontwerp: docs/Ontwerp_AI_Radar.md
import { useCallback, useEffect, useState } from "react";
import { supabase } from "../supabase";
import { C, Kop, Tellers, Groep, Rij, Leeg, Chip, Melding, eur } from "./gedeeld";
import { SOORTEN, AANBIEDERS, pct, kansTekst, kansKleur, verschil, verschilTekst } from "./radarHulp";

const GEEN = "Geen gegevens beschikbaar";
const dtm = (iso) => (iso ? new Date(iso).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

export default function AiRadar() {
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState(null);
  const [sam, setSam] = useState(null);
  const [vragen, setVragen] = useState([]);
  const [alarmen, setAlarmen] = useState([]);
  const [leesb, setLeesb] = useState(null);
  const [fout, setFout] = useState(null);
  const [toast, setToast] = useState(null);
  const [bezig, setBezig] = useState(null);
  const [nieuw, setNieuw] = useState({ soort: "citatie", tekst: "", cluster: "algemeen" });
  const [feiten, setFeiten] = useState({ merkfeiten: "", sector: "", plaats: "" });

  const melding = (m) => { setToast(m); setTimeout(() => setToast(null), 3500); };
  const site = sites.find((s) => s.id === siteId);

  const laadSites = useCallback(async () => {
    const { data, error } = await supabase.from("seo_sites").select("id,naam,domein,merkfeiten,sector,plaats").eq("actief", true).eq("bevestigd_eigen", true).order("naam");
    if (error) { setFout(error.message); return; }
    setSites(data || []);
    setSiteId((cur) => cur || (data && data[0] && data[0].id) || null);
  }, []);

  const laad = useCallback(async () => {
    if (!siteId) return;
    try {
      const [s, v, a, l] = await Promise.all([
        supabase.rpc("radar_samenvatting", { p_site: siteId }),
        supabase.from("radar_vragen").select("*").eq("site_id", siteId).order("soort").order("created_at"),
        supabase.from("radar_alarmen").select("*").eq("site_id", siteId).order("created_at", { ascending: false }).limit(5),
        supabase.from("radar_leesbaarheid").select("*").eq("site_id", siteId).order("gemeten_op", { ascending: false }).limit(1),
      ]);
      const e = [s, v, a, l].find((x) => x.error);
      if (e) throw new Error(e.error.message);
      setSam(s.data); setVragen(v.data || []); setAlarmen(a.data || []); setLeesb((l.data || [])[0] || null); setFout(null);
    } catch (e) { setFout(e.message); }
  }, [siteId]);

  useEffect(() => { laadSites(); }, [laadSites]);
  useEffect(() => { laad(); }, [laad]);
  useEffect(() => { if (site) setFeiten({ merkfeiten: site.merkfeiten || "", sector: site.sector || "", plaats: site.plaats || "" }); }, [site]);
  // Zolang er metingen wachten ververst het scherm zichzelf
  useEffect(() => {
    if (!sam || !sam.wacht) return undefined;
    const t = setInterval(laad, 20000);
    return () => clearInterval(t);
  }, [sam, laad]);

  async function actie(naam, body, ok) {
    setBezig(naam);
    try {
      const { data, error } = await supabase.functions.invoke("ai-radar", { body: { ...body, site_id: siteId } });
      if (error) {
        let tekst = error.message;
        try { const j = await error.context.json(); if (j && j.error) tekst = j.error; } catch (e) { /* standaardmelding */ }
        throw new Error(tekst);
      }
      melding(typeof ok === "function" ? ok(data) : ok);
      await laad();
    } catch (e) { melding("✗ " + e.message); } finally { setBezig(null); }
  }

  async function bewaarFeiten() {
    const { error } = await supabase.from("seo_sites").update({ merkfeiten: feiten.merkfeiten.trim() || null, sector: feiten.sector.trim() || null, plaats: feiten.plaats.trim() || null }).eq("id", siteId);
    if (error) { melding("✗ " + error.message); return; }
    melding("✓ Merkgegevens opgeslagen"); laadSites();
  }

  async function voegVraagToe() {
    const tekst = nieuw.tekst.trim();
    if (tekst.length < 5) { melding("✗ Schrijf de vraag voluit (minimaal 5 tekens)"); return; }
    const { error } = await supabase.from("radar_vragen").insert([{ site_id: siteId, soort: nieuw.soort, tekst, cluster: (nieuw.cluster.trim() || "algemeen").slice(0, 40), bron: "handmatig" }]);
    if (error) { melding("✗ " + (error.code === "23505" ? "Deze vraag bestaat al" : error.message)); return; }
    setNieuw({ ...nieuw, tekst: "" }); melding("✓ Vraag toegevoegd"); laad();
  }

  async function zetActief(v, actief) {
    const { error } = await supabase.from("radar_vragen").update({ actief }).eq("id", v.id);
    if (error) melding("✗ " + error.message); else laad();
  }

  const perSoort = (sam && sam.per_soort) || [];
  const rondeKlaar = sam && sam.laatste_ronde && ["klaar", "deels"].includes(sam.laatste_ronde.status);
  const lr = sam && sam.laatste_ronde;

  return (
    <div className="fu">
      {toast && <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: toast.startsWith("✗") ? C.redDim : C.greenDim, border: "1px solid " + (toast.startsWith("✗") ? "rgba(229,86,78,.3)" : "rgba(52,189,130,.25)"), color: toast.startsWith("✗") ? C.red : C.green, padding: "10px 18px", borderRadius: 8, fontSize: 13, zIndex: 1000 }}>{toast}</div>}
      <Kop titel="AI-radar" sub="Wordt het merk geciteerd, onthouden en gekozen door AI en door AI-agents? Elke uitkomst is een kans met marge, uit meerdere metingen." rechts={<>
        <select className="inp" value={siteId || ""} onChange={(e) => setSiteId(e.target.value)} style={{ minWidth: 160 }}>
          {sites.map((s) => <option key={s.id} value={s.id}>{s.naam}</option>)}
        </select>
        <button className="btn" disabled={!!bezig || !siteId || (sam && sam.wacht > 0)} onClick={() => actie("start", { action: "start" }, (d) => `✓ Ronde gestart: ${d.gestart} metingen`)}>{bezig === "start" ? "Starten..." : "Start meetronde"}</button>
      </>} />

      {fout && <Melding type="fout">{fout}</Melding>}
      {fout && /does not exist|schema cache|radar_/i.test(fout) && <Melding type="let-op">De migratie ai_radar.sql is nog niet gedraaid.</Melding>}

      {sam && sam.wacht > 0 && <Melding type="info">Er lopen nog {sam.wacht} metingen. Het scherm ververst zichzelf. Het dagbudget bepaalt hoeveel er per dag worden uitgevoerd; wat overblijft gaat morgen verder.</Melding>}

      <Tellers items={[
        { label: "Geciteerd", waarde: pct((perSoort.find((p) => p.soort === "citatie") || {}).kans) },
        { label: "Onthouden", waarde: pct((perSoort.find((p) => p.soort === "onthouden") || {}).kans) },
        { label: "Gekozen door agents", waarde: pct((perSoort.find((p) => p.soort === "agent") || {}).kans) },
        { label: "Kosten 30 dagen", waarde: sam ? eur(sam.kosten_30d) : "-" },
      ]} />

      {!rondeKlaar && !(sam && sam.wacht) && <Leeg tekst={lr ? "De laatste ronde is niet afgerond." : "Nog geen metingen. Start een meetronde: de radar zaait zelf standaardvragen voor deze site."} />}

      {alarmen.length > 0 && (
        <Groep label="Alarmen" kleur={C.red} aantal={alarmen.length}>
          {alarmen.map((a) => <Rij key={a.id} kleur={C.red} titel={a.toelichting} meta={<span>{dtm(a.created_at)}{a.proposal_id ? " · voorstel staat in de wachtrij" : ""}</span>} />)}
        </Groep>
      )}

      {rondeKlaar && (<>
        <div style={{ height: 10 }} />
        <Groep label="Uitkomst per soort" kleur={C.blue} aantal={perSoort.length}>
          {Object.keys(SOORTEN).map((k) => {
            const r = perSoort.find((p) => p.soort === k);
            const per = ((sam.per_aanbieder) || []).filter((p) => p.soort === k);
            return (
              <Rij key={k} titel={SOORTEN[k].label} kleur={kansKleur(r, C)} meta={<span>{kansTekst(r)} · {verschilTekst(verschil(sam.trend, k))}</span>} voorbeeld={SOORTEN[k].uitleg}>
                <p style={{ fontSize: 12, color: C.muted, margin: "0 0 8px" }}>{SOORTEN[k].uitleg}</p>
                {per.length === 0 ? <p style={{ fontSize: 12, color: C.muted }}>{GEEN}</p> : per.map((p) => (
                  <div key={p.aanbieder} style={{ display: "flex", gap: 8, fontSize: 12, padding: "3px 0" }}>
                    <span style={{ width: 90, color: C.text }}>{AANBIEDERS[p.aanbieder] || p.aanbieder}</span>
                    <span style={{ color: kansKleur(p, C) }}>{kansTekst(p)}</span>
                  </div>
                ))}
                {((sam.per_cluster) || []).filter((c) => c.soort === k).length > 0 && <p className="lbl" style={{ margin: "10px 0 4px" }}>Per vraagcluster</p>}
                {((sam.per_cluster) || []).filter((c) => c.soort === k).map((c) => (
                  <div key={c.cluster} style={{ display: "flex", gap: 8, fontSize: 12, padding: "3px 0" }}>
                    <span style={{ width: 90, color: C.text }}>{c.cluster}</span><span style={{ color: C.muted }}>{pct(c.kans)} ({c.n} metingen)</span>
                  </div>
                ))}
              </Rij>
            );
          })}
        </Groep>

        <div style={{ height: 10 }} />
        <Groep label="Wie wordt wel genoemd" kleur={C.yellow} aantal={(sam.concurrenten || []).length}>
          {(sam.concurrenten || []).length === 0 ? <Leeg tekst={GEEN} /> : sam.concurrenten.map((c) => <Rij key={c.naam} titel={c.naam} meta={<span>{c.aantal} keer · {pct(c.aandeel)} van de metingen</span>} />)}
        </Groep>

        <div style={{ height: 10 }} />
        <Groep label="Wat agents missen om te kiezen" kleur={C.yellow} aantal={(sam.ontbreekt || []).length} standaardDicht>
          {(sam.ontbreekt || []).length === 0 ? <Leeg tekst="Nog geen agent-metingen of niets gemist." /> : sam.ontbreekt.map((o) => <Rij key={o.punt} titel={o.punt} meta={<span>{o.aantal} keer genoemd</span>} />)}
        </Groep>

        <div style={{ height: 10 }} />
        <Groep label="Welke bronnen AI gebruikt" kleur={C.muted} aantal={(sam.bron_domeinen || []).length} standaardDicht>
          {(sam.bron_domeinen || []).length === 0 ? <Leeg tekst={GEEN} /> : sam.bron_domeinen.map((b) => <Rij key={b.domein} titel={b.domein} meta={<span>{b.aantal} keer</span>} />)}
        </Groep>
      </>)}

      <div style={{ height: 10 }} />
      <Groep label="Leesbaar voor AI en agents" kleur={leesb ? (leesb.score >= 70 ? C.green : C.yellow) : C.muted} aantal={leesb ? leesb.score + "/100" : null} standaardDicht={!leesb}
        voet={<div style={{ padding: "0 12px 12px" }}><button className="btn" disabled={!!bezig || !siteId} onClick={() => actie("lees", { action: "leesbaarheid" }, (d) => `✓ Score ${d.score} van 100`)}>{bezig === "lees" ? "Controleren..." : "Controleer nu"}</button></div>}>
        {!leesb ? <Leeg tekst="Nog niet gecontroleerd. De controle kijkt naar robots.txt, llms.txt, sitemap en gestructureerde gegevens van de eigen site." /> :
          (leesb.checks || []).map((c) => <Rij key={c.sleutel} kleur={c.ok ? C.green : C.red} titel={c.uitleg} meta={<span>{c.gewicht ? c.gewicht + " punten" : "voorwaarde"}</span>} />)}
      </Groep>

      <div style={{ height: 10 }} />
      <Groep label="Merkgegevens voor de metingen" kleur={C.blue} standaardDicht>
        <div style={{ padding: 12, display: "grid", gap: 8 }}>
          <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>Merkfeiten zijn de referentie voor "Onthouden": alleen wat jij hier invult geldt als juist. Sector en plaats maken de agent-opdracht (bijvoorbeeld: "Zoek een automatiseringsbureau voor het MKB in Utrecht").</p>
          <textarea className="inp" rows={3} placeholder="Merkfeiten: wat het bedrijf is en doet, in een paar zinnen" value={feiten.merkfeiten} onChange={(e) => setFeiten({ ...feiten, merkfeiten: e.target.value })} />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input className="inp" style={{ flex: 2, minWidth: 180 }} placeholder="Sector, bijvoorbeeld: automatiseringsbureau" value={feiten.sector} onChange={(e) => setFeiten({ ...feiten, sector: e.target.value })} />
            <input className="inp" style={{ flex: 1, minWidth: 120 }} placeholder="Plaats (optioneel)" value={feiten.plaats} onChange={(e) => setFeiten({ ...feiten, plaats: e.target.value })} />
            <button className="btn" onClick={bewaarFeiten}>Opslaan</button>
          </div>
        </div>
      </Groep>

      <div style={{ height: 10 }} />
      <Groep label="Vragen" kleur={C.muted} aantal={vragen.filter((v) => v.actief).length} standaardDicht>
        {vragen.length === 0 ? <Leeg tekst="Nog geen vragen. De eerste meetronde zaait standaardvragen." /> : vragen.map((v) => (
          <Rij key={v.id} gedimd={!v.actief} titel={v.tekst} meta={<><Chip tekst={SOORTEN[v.soort].label} /><span>{v.cluster}</span></>}
            rechts={<span className="btn" style={{ fontSize: 11 }} onClick={(e) => { e.stopPropagation(); zetActief(v, !v.actief); }}>{v.actief ? "Uit" : "Aan"}</span>} />
        ))}
        <div style={{ padding: 12, borderTop: "1px solid " + C.border, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select className="inp" value={nieuw.soort} onChange={(e) => setNieuw({ ...nieuw, soort: e.target.value })}>
            {Object.keys(SOORTEN).map((k) => <option key={k} value={k}>{SOORTEN[k].label}</option>)}
          </select>
          <input className="inp" style={{ flex: 3, minWidth: 220 }} placeholder="Nieuwe vraag, zoals een klant hem zou stellen" value={nieuw.tekst} onChange={(e) => setNieuw({ ...nieuw, tekst: e.target.value })} />
          <input className="inp" style={{ flex: 1, minWidth: 90 }} placeholder="Cluster" value={nieuw.cluster} onChange={(e) => setNieuw({ ...nieuw, cluster: e.target.value })} />
          <button className="btn" onClick={voegVraagToe}>Toevoegen</button>
        </div>
      </Groep>

      {lr && <p style={{ fontSize: 11, color: C.dim, marginTop: 12 }}>Laatste ronde: {dtm(lr.gestart_op)} · {lr.status} · {lr.gepland} metingen gepland · {eur(lr.kosten_eur)}. Antwoorden worden niet volledig bewaard, alleen een afdruk en een kort fragment.</p>}
    </div>
  );
}
